import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	ModalNextReachoutDateData,
	ModalNextReachoutDateFormProps,
} from "../../models/components/modal-next-reachout-date.model";
import UiButton from "../button/Button";
import UiFieldMessage from "../field-message/FieldMessage.component";
import { Form } from "react-final-form";
import FormSection from "../form-section/FormSection.component";
import UiInputDate from "../input-date/InputDate.component";
import { Validations } from "../../services/form/validations.service";
import { Formatter } from "../../services/formatter/formatter.service";
import { FormApi } from "final-form";
import UiSelectButton from "../select-button/SelectButton.component";

const ModalNextReachoutDateForm = (props: ModalNextReachoutDateFormProps) => {
	const { t } = useTranslation("common");

	const initialFormData: ModalNextReachoutDateData = {
		dateOn: props.initialDate || null,
		optionValue: 0,
		formMessage: undefined,
	};

	const [formData, setFormData] = useState<ModalNextReachoutDateData>({ ...initialFormData });
	const formApiRef = useRef<FormApi<
		ModalNextReachoutDateData,
		Partial<ModalNextReachoutDateData>
	> | null>(null);

	// Sync formData.dateOn with form field when it changes (for date options)
	useEffect(() => {
		if (formApiRef.current && formData.dateOn) {
			const currentFieldValue = formApiRef.current.getFieldState("dateOn")?.value;
			if (currentFieldValue?.getTime() !== formData.dateOn.getTime()) {
				formApiRef.current.change("dateOn", formData.dateOn);
			}
		}
	}, [formData.dateOn]);

	const dateOptions = [
		{ id: 1, label: "UI_COMPONENTS.CALENDAR.ONE_DAY" },
		{ id: 3, label: "UI_COMPONENTS.CALENDAR.THREE_DAYS" },
		{ id: 5, label: "UI_COMPONENTS.CALENDAR.FIVE_DAYS" },
		{ id: 7, label: "UI_COMPONENTS.CALENDAR.ONE_WEEK" },
		{ id: 14, label: "UI_COMPONENTS.CALENDAR.TWO_WEEKS" },
		{ id: 30, label: "UI_COMPONENTS.CALENDAR.ONE_MONTH" },
		{ id: 0, label: "UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.CUSTOM" },
	];

	// Submit
	const handleSubmit = async (
		data: ModalNextReachoutDateData,
		form: FormApi<ModalNextReachoutDateData, Partial<ModalNextReachoutDateData>>
	) => {
		// Reset login error
		setFormData({ ...data, formMessage: undefined });
		props.onComplete(data.dateOn);
	};

	const calculateDateFromOption = (e: any) => {
		let nextDate = new Date();
		const dayToMilliseconds = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
		if (e.value !== 0) {
			nextDate = new Date(nextDate.getTime() + e.target.value * dayToMilliseconds);
			// Adjust to next working day if weekend
			nextDate = Formatter.getNextWorkingDate(nextDate)!;
		}

		setFormData({
			...formData,
			dateOn: nextDate,
			optionValue: e.target.value,
		});
	};

	return (
		<Form
			onSubmit={handleSubmit}
			initialValues={formData}
			render={({ handleSubmit, form, submitting }) => {
				// Store form API ref for useEffect
				formApiRef.current = form;
				return (
					<form
						onSubmit={handleSubmit}
						className="modal-next-reachout-date"
					>
						<FormSection className="row">
							{/* Date Picker and Options Container */}
							<div className="col-12 col-sm-12 col-md-12 col-lg-8">
								<div className="participant-modal-reminders__date-input-container">
									{/* Date Picker - Left Section */}
									<div>
										<UiInputDate
											id="dateOn"
											name="dateOn"
											label="UI_COMPONENTS.MODAL_NEXT_REACHOUT_DATE.FORM_FIELDS.dateOn"
											onChange={(e) => {
												const selectedDate = e.target.value as Date;
												// Don't adjust weekend dates when manually selected
												setFormData({
													...formData,
													dateOn: selectedDate,
													optionValue: 0,
												});
											}}
											minDate={new Date()}
											validations={[]}
											disabled={submitting}
											inline={true}
										/>
										<p
											className="mt-2"
											style={{ fontSize: "14px" }}
										>
											{t(
												"UI_COMPONENTS.MODAL_NEXT_REACHOUT_DATE.FORM_FIELDS.DATE_WILL_BE_SET_TO"
											)}{" "}
											{formData.dateOn ? (
												<span
													style={{
														fontWeight: "bold",
														color: "var(--color-gray-darker)",
													}}
												>
													{Formatter.date(formData.dateOn.toISOString())}
												</span>
											) : (
												<span style={{ color: "var(--color-gray-darker)" }}>
													-
												</span>
											)}
										</p>
									</div>

									{/* Quick Options - Right Section */}
									<div
										className="col-12 col-sm-12 col-md-12 col-lg-6"
										style={{ minWidth: "250px", flex: "0 0 auto" }}
									>
										<p className="secondary-title">
											{t(
												"UI_COMPONENTS.MODAL_NEXT_REACHOUT_DATE.FORM_FIELDS.OPTIONS"
											)}
										</p>
										<div style={{ width: "100%" }}>
											<UiSelectButton
												id="optionValue"
												className="vertical"
												name="optionValue"
												onChange={calculateDateFromOption}
												options={dateOptions}
											/>
										</div>
									</div>
								</div>
							</div>
						</FormSection>

						{formData.formMessage && (
							<div className="form-message">
								<UiFieldMessage {...formData.formMessage}></UiFieldMessage>
							</div>
						)}

						<div className="action-buttons">
							<UiButton
								label="UI_COMPONENTS.BUTTONS.CANCEL"
								className="p-button-outlined p-button-rounded"
								type="button"
								onClick={props.onCancel}
								disabled={submitting}
							/>
							<UiButton
								label="UI_COMPONENTS.BUTTONS.CONFIRM"
								className="p-button-rounded"
								type="submit"
								loading={submitting}
								disabled={submitting}
							/>
						</div>
					</form>
				);
			}}
		/>
	);
};

export default ModalNextReachoutDateForm;
